/**
 * Mirror of `app/core/permissions.py`.
 *
 * These strings gate **UI affordances only** — what a user is offered, not what
 * they are allowed to do. The backend re-checks every one of them; a mismatch
 * here can hide a button or surface a 403, never grant access.
 */
export const PERMISSIONS = {
  COMPLEX_VIEW: 'complex.view',
  COMPLEX_CREATE: 'complex.create',
  COMPLEX_UPDATE: 'complex.update',
  COMPLEX_DELETE: 'complex.delete',

  BUILDING_VIEW: 'building.view',
  BUILDING_CREATE: 'building.create',
  BUILDING_UPDATE: 'building.update',
  BUILDING_DELETE: 'building.delete',

  UNIT_VIEW: 'unit.view',
  UNIT_CREATE: 'unit.create',
  UNIT_UPDATE: 'unit.update',
  UNIT_DELETE: 'unit.delete',

  MEMBER_VIEW: 'member.view',
  MEMBER_CREATE: 'member.create',
  MEMBER_UPDATE: 'member.update',
  MEMBER_DELETE: 'member.delete',

  CHARGE_VIEW: 'charge.view',
  CHARGE_CREATE: 'charge.create',
  CHARGE_UPDATE: 'charge.update',
  CHARGE_DELETE: 'charge.delete',

  PAYMENT_VIEW: 'payment.view',
  PAYMENT_CREATE: 'payment.create',
  PAYMENT_CONFIRM: 'payment.confirm',
  PAYMENT_CANCEL: 'payment.cancel',

  REPORT_VIEW: 'report.view',
  NOTIFICATION_SEND: 'notification.send',

  MANAGER_ASSIGN: 'manager.assign',
  MANAGER_REMOVE: 'manager.remove',

  USER_VIEW: 'user.view',
  USER_UPDATE: 'user.update',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Seeded system roles (`app/db/seeds/seed_roles.py`). */
export const SYSTEM_ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  COMPLEX_MANAGER: 'ComplexManager',
  BUILDING_MANAGER: 'BuildingManager',
} as const;

export type SystemRoleName = (typeof SYSTEM_ROLES)[keyof typeof SYSTEM_ROLES];

/**
 * Client-side copy of `seed_role_permissions.py`.
 *
 * `/v1/users/me/access` returns role *names* and scopes but not the underlying
 * permissions, so resolving "can this user create a building here?" requires
 * knowing the role→permission mapping. Keeping it as data — rather than
 * scattering `role === 'ComplexManager'` checks through the UI — means adding a
 * role later touches this table and nothing else.
 *
 * SuperAdmin is deliberately absent: it is handled by the `is_superadmin` flag,
 * which short-circuits every check.
 */
export const ROLE_PERMISSIONS: Readonly<Record<string, readonly Permission[]>> = {
  [SYSTEM_ROLES.SUPER_ADMIN]: Object.values(PERMISSIONS),
  [SYSTEM_ROLES.COMPLEX_MANAGER]: [
    PERMISSIONS.COMPLEX_VIEW,
    PERMISSIONS.COMPLEX_UPDATE,
    // The self-service creator is the top authority of their own complex.
    PERMISSIONS.COMPLEX_DELETE,
    PERMISSIONS.BUILDING_VIEW,
    PERMISSIONS.BUILDING_CREATE,
    PERMISSIONS.BUILDING_UPDATE,
    PERMISSIONS.BUILDING_DELETE,
    PERMISSIONS.UNIT_VIEW,
    PERMISSIONS.UNIT_CREATE,
    PERMISSIONS.UNIT_UPDATE,
    PERMISSIONS.UNIT_DELETE,
    PERMISSIONS.MEMBER_VIEW,
    PERMISSIONS.MEMBER_CREATE,
    PERMISSIONS.MEMBER_UPDATE,
    PERMISSIONS.MEMBER_DELETE,
    PERMISSIONS.CHARGE_VIEW,
    PERMISSIONS.CHARGE_CREATE,
    PERMISSIONS.CHARGE_UPDATE,
    PERMISSIONS.CHARGE_DELETE,
    PERMISSIONS.PAYMENT_VIEW,
    PERMISSIONS.PAYMENT_CREATE,
    PERMISSIONS.PAYMENT_CONFIRM,
    PERMISSIONS.PAYMENT_CANCEL,
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.NOTIFICATION_SEND,
    PERMISSIONS.MANAGER_ASSIGN,
    PERMISSIONS.MANAGER_REMOVE,
    PERMISSIONS.USER_VIEW,
  ],
  [SYSTEM_ROLES.BUILDING_MANAGER]: [
    PERMISSIONS.BUILDING_VIEW,
    PERMISSIONS.BUILDING_UPDATE,
    PERMISSIONS.BUILDING_DELETE,
    PERMISSIONS.UNIT_VIEW,
    PERMISSIONS.UNIT_CREATE,
    PERMISSIONS.UNIT_UPDATE,
    PERMISSIONS.UNIT_DELETE,
    PERMISSIONS.MEMBER_VIEW,
    PERMISSIONS.MEMBER_CREATE,
    PERMISSIONS.MEMBER_UPDATE,
    PERMISSIONS.MEMBER_DELETE,
    PERMISSIONS.CHARGE_VIEW,
    PERMISSIONS.CHARGE_CREATE,
    PERMISSIONS.CHARGE_UPDATE,
    // Deliberately no CHARGE_DELETE — only a complex manager may erase a
    // building's billing history.
    PERMISSIONS.PAYMENT_VIEW,
    PERMISSIONS.PAYMENT_CREATE,
    PERMISSIONS.PAYMENT_CONFIRM,
    PERMISSIONS.PAYMENT_CANCEL,
    PERMISSIONS.REPORT_VIEW,
    PERMISSIONS.NOTIFICATION_SEND,
    PERMISSIONS.USER_VIEW,
  ],
};

/** Persian labels for the seeded roles, used in pickers and badges. */
export const ROLE_LABELS: Readonly<Record<string, string>> = {
  [SYSTEM_ROLES.SUPER_ADMIN]: 'مدیر سیستم',
  [SYSTEM_ROLES.COMPLEX_MANAGER]: 'مدیر مجتمع',
  [SYSTEM_ROLES.BUILDING_MANAGER]: 'مدیر ساختمان',
};

export function roleLabel(roleName: string): string {
  return ROLE_LABELS[roleName] ?? roleName;
}
